<script setup lang="ts">
import { computed } from 'vue';
import { recordLinkFieldLabel } from '@/components/domain/record-link/fields';
import { useI18n } from '@/composables/i18n';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    records: DifferingRecord[]
    fields: string[]
    divergentFields: string[]
}>();

export interface DifferingRecord {
    institutionName: string
    birthName?: string | null
    values: Record<string, string | null>
}

interface DivergentValue {
    text: string
    clubs: string
}

interface DivergentField {
    field: string
    label: string
    values: DivergentValue[]
}

const { i18n } = useI18n();

const EMPTY = '—';

function displayValue(field: string, value: string | null | undefined): string {
    if (value === null || value === undefined || value.trim() === '') {
        return EMPTY;
    }

    return field === 'gender' ? i18n.t(value) : value;
}

const WHITESPACE = /\s+/g;

/** Mirrors ComparedValues::normalize on the server. */
const normalize = (value: string | null | undefined): string => (value ?? '').trim().replace(WHITESPACE, ' ').toLowerCase();

/** A last name shows the birth name the scan may have matched instead. */
function fieldText(field: string, record: DifferingRecord): string {
    const text = displayValue(field, record.values[field]);
    const birthName = (record.birthName ?? '').trim();
    if (field !== 'lastName' || text === EMPTY || birthName === '' || normalize(birthName) === normalize(record.values[field])) {
        return text;
    }

    return i18n.t('{lastName} (geb. {birthName})', { lastName: text, birthName });
}

function clubsLabel(clubs: string[]): string {
    return clubs.length > 2
        ? i18n.t('{count} Vereine ({first} …)', { count: clubs.length, first: clubs[0] })
        : clubs.join(', ');
}

/** Distinct spellings of one field, in record order: values the scan judged identical can still read differently. */
function distinctTexts(field: string): string[] {
    const texts = new Map<string, string>();
    for (const record of props.records) {
        const text = fieldText(field, record);
        if (!texts.has(normalize(text))) {
            texts.set(normalize(text), text);
        }
    }

    return Array.from(texts.values());
}

/** A matching last name matched through a birth name lists both spellings, e.g. "Meier (geb. Schulz) / Schulz". */
const matchingValues = computed(() => props.fields
    .filter((field) => !props.divergentFields.includes(field))
    .map((field) => distinctTexts(field).filter((text) => text !== EMPTY).join(' / '))
    .filter((value) => value !== ''));

const divergentFields = computed<DivergentField[]>(() => props.divergentFields.map((field) => {
    const groups = new Map<string, { text: string, clubs: string[] }>();
    for (const record of props.records) {
        const text = fieldText(field, record);
        const group = groups.get(normalize(text));
        if (group) {
            group.clubs.push(record.institutionName);
        } else {
            groups.set(normalize(text), { text, clubs: [record.institutionName] });
        }
    }

    return {
        field,
        label: recordLinkFieldLabel(field),
        values: Array.from(groups.values(), (group) => ({ text: group.text, clubs: clubsLabel(group.clubs) })),
    };
}));
</script>

<template>
    <div class="record-differences">
        <div
            v-if="matchingValues.length > 0"
            class="record-differences__field record-differences__field--matching"
        >
            <h4 class="kgr-headline kgr-headline--h6 record-differences__headline">
                {{ $t('Übereinstimmend') }}
            </h4>
            <div class="text-sm">
                {{ matchingValues.join(' · ') }}
            </div>
        </div>
        <div
            v-for="divergent in divergentFields"
            :key="divergent.field"
            class="record-differences__field record-differences__field--divergent"
        >
            <h4 class="kgr-headline kgr-headline--h6 record-differences__headline">
                {{ $t(divergent.label) }}
            </h4>
            <div class="record-differences__values">
                <div
                    v-for="value in divergent.values"
                    :key="value.text"
                    class="record-differences__value"
                >
                    <span class="kgr-text kgr-text--light kgr-text--small">{{ value.clubs }}</span>
                    <strong class="text-base">{{ value.text }}</strong>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.record-differences {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    min-width: 0;
    overflow-wrap: anywhere;
}

.record-differences__field {
    padding-left: 0.625rem;
    border-left: 2px solid var(--record-differences-color);
}

.record-differences__field--matching {
    --record-differences-color: var(--kgr-color-success);
}

.record-differences__field--divergent {
    --record-differences-color: var(--kgr-color-warning);
}

.record-differences__headline {
    margin-bottom: 0.25rem;
    color: var(--record-differences-color);
}

.record-differences__values {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(9rem, 100%), 1fr));
    gap: 0.5rem;
}

.record-differences__value {
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 0.375rem 0.5rem;
    border-radius: var(--radius-md);
    background-color: color-mix(in srgb, var(--kgr-color-warning) 12%, transparent);
}
</style>
